import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'toro-decimal-input',
    templateUrl: './decimal-input.component.html',
    styleUrls: ['./decimal-input.component.less'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DecimalInputComponent),
            multi: true
        }
    ]
})
export class DecimalInputComponent implements ControlValueAccessor, OnInit, OnChanges {
    @Input() min = Number.NEGATIVE_INFINITY;
    @Input() max = Number.POSITIVE_INFINITY;
    @Input() minDecimal = 0;
    @Input() maxDecimal = 2;
    @Input() step = 1;
    @Input() disabled = false;
    @Input() width = 70;
    @Input() allowNegative = false;

    @Input() decimalSeparator = '.';
    @Input() groupSeparator = ',';
    @Input() useGrouping = false;

    @Output() valueChange = new EventEmitter<number | null>();

    @Input()
    get value(): number | null {
        return this._value;
    }

    set value(val: number | null | boolean) {
        if (val === null || val === undefined || val === false) {
            this.setInternalValue(null);
        } else {
            this.setInternalValue(this.clampAndFormat(Number(val)));
        }
    }

    protected rawInput = '';

    private _value: number | null = null;
    private inputSubject = new Subject<string>();

    onChange = (_: any) => {};
    onTouched = () => {};

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor() {
        this.inputSubject
            .pipe(
                debounceTime(500),
                untilDestroyed(this)
            )
            .subscribe(val => {
                this.formatAndEmit(val, false, false);
            });
    }

    ngOnInit() {
        this.rawInput = this.formatForDisplay(this._value, true);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['decimalSeparator'] ||
            changes['groupSeparator'] ||
            changes['useGrouping'] ||
            changes['maxDecimal']
        ) {
            this.rawInput = this.formatForDisplay(this._value, true);
        }
    }

    // =========================================================================================================================================================
    // ControlValueAccessor Methods
    // =========================================================================================================================================================

    writeValue(val: number | null): void {
        this.value = val;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    onInput(event: any): void {
        const val: string = event.target.value;

        if (!this.isValidInput(val)) {
            event.target.value = this.rawInput;
            return;
        }

        const parsed = this.parseInput(val);

        if (!this.allowNegative && parsed !== null && parsed < 0) {
            event.target.value = this.rawInput;
            return;
        }

        if (parsed !== null && parsed > this.max) {
            event.target.value = this.rawInput;
            return;
        }

        this.rawInput = val;
        this.inputSubject.next(this.rawInput);
    }

    onBlur() {
        this.formatAndEmit(this.rawInput, true);
        this.onTouched();
    }

    increment() {
        if (this.disabled) return;

        const newValue = (this._value ?? 0) + this.step;
        this.setInternalValue(this.clampAndFormat(newValue));
        this.emitValue();
    }

    decrement() {
        if (this.disabled) return;

        const newValue = (this._value ?? 0) - this.step;
        this.setInternalValue(this.clampAndFormat(newValue));
        this.emitValue();
    }

    // =========================================================================================================================================================
    // Private Methods
    // =========================================================================================================================================================

    private formatAndEmit(val: string, forceFormat = false, pad = true): void {
        const parsed = this.parseInput(val);

        if (val === '' || parsed === null) {
            this.setInternalValue(null);
        } else {
            this.setInternalValue(this.clampAndFormat(parsed), pad);
        }

        if (forceFormat) {
            this.rawInput = this.formatForDisplay(this._value, true);
        }

        this.emitValue();
    }

    private setInternalValue(val: number | null, pad = true): void {
        if (this._value === val) {
            if (val !== null) {
                this.rawInput = this.formatForDisplay(val, pad);
            } else {
                this.rawInput = '';
            }
            return;
        }

        this._value = val;

        if (val === null) {
            this.rawInput = '';
            return;
        }

        this.rawInput = this.formatForDisplay(val, pad);
    }

    private emitValue() {
        this.valueChange.emit(this._value);
        this.onChange(this._value);
    }

    private clampAndFormat(val: number | null): number | null {
        if (val === null || isNaN(val)) return null;

        let clamped = Math.max(this.min, Math.min(this.max, val));
        const factor = Math.pow(10, this.maxDecimal);
        clamped = Math.round(clamped * factor) / factor;

        return clamped;
    }

    private parseInput(val: string): number | null {
        if (!val?.trim()) return null;

        let normalized = val.trim();

        if (this.groupSeparator) {
            normalized = normalized.split(this.groupSeparator).join('');
        }

        if (this.decimalSeparator && this.decimalSeparator !== '.') {
            normalized = normalized.replace(this.decimalSeparator, '.');
        }

        // Support inputs like ".35" after normalization
        if (normalized.startsWith('.')) {
            normalized = `0${normalized}`;
        } else if (normalized.startsWith('-.')) {
            normalized = normalized.replace('-.', '-0.');
        }

        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private formatForDisplay(val: number | null, pad: boolean): string {
        if (val === null || isNaN(val)) {
            return '';
        }

        let base: string;

        if (pad) {
            base = val.toFixed(this.maxDecimal);
        } else if (Number.isInteger(val)) {
            base = val.toString();
        } else {
            const str = val.toString();
            const [, decPart] = str.split('.');
            base = decPart && decPart.length > this.maxDecimal
                ? val.toFixed(this.maxDecimal)
                : str;
        }

        let [intPart, decPart] = base.split('.');

        if (this.useGrouping) {
            intPart = this.addGrouping(intPart);
        }

        if (decPart == null || decPart.length === 0) {
            return intPart;
        }

        return `${intPart}${this.decimalSeparator}${decPart}`;
    }

    private addGrouping(value: string): string {
        const isNegative = value.startsWith('-');
        const digits = isNegative ? value.substring(1) : value;

        const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, this.groupSeparator);
        return isNegative ? `-${grouped}` : grouped;
    }

    private isValidInput(val: string): boolean {
        if (val === '') return true;
        if (this.allowNegative && val === '-') return true;

        const escapedDecimal = this.escapeRegExp(this.decimalSeparator);
        const escapedGroup = this.escapeRegExp(this.groupSeparator);

        const signPart = this.allowNegative ? '-?' : '';

        const integerPart = this.useGrouping
            ? `(?:\\d{1,3}(?:${escapedGroup}\\d{3})*|\\d+)`
            : `\\d+`;

        // Allow:
        //  - whole numbers: 123
        //  - decimal with integer part: 123,45
        //  - decimal without integer part: ,45
        //  - trailing decimal while typing: 123,
        //  - just decimal separator while typing: ,
        const pattern = this.maxDecimal > 0
            ? `^${signPart}(?:${integerPart}(?:${escapedDecimal}\\d{0,${this.maxDecimal}})?|${escapedDecimal}\\d{0,${this.maxDecimal}})?$`
            : `^${signPart}(?:${integerPart})?$`;

        return new RegExp(pattern).test(val);
    }

    private escapeRegExp(value: string): string {
        return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
}
